const DEFAULT_BUTTON_THEME = {
  defaultBg: "#f0833c",
  defaultText: "#ffffff",
  defaultHoverBg: "#0d102a",
  defaultHoverText: "#ffffff",
  outlineBg: "#ffffff",
  outlineText: "#5e72e4",
};

const DEFAULT_LINKS = {
  terms: process.env.VUE_APP_TERMS_URL || "https://rpay.inc/wp-content/uploads/2023/02/Terms-and-Conditions-RocketFuel-1.pdf",
  privacy: process.env.VUE_APP_PRIVACY_URL || "https://rpay.inc/wp-content/uploads/2023/02/Privacy-Policy-RocketFuel-v3-2023-1.pdf",
};

/**
 * Read a single whitelabel color property from marketInfo.colorSchema.colors.
 */
export function getColorProperty(
  marketInfo,
  sectionName,
  childName,
  propertyName,
  fallback
) {
  const colors = marketInfo?.whiteLabel?.colorSchema?.colors;
  if (!colors?.length) {
    return fallback;
  }
  const section = colors.find((el) => el.name === sectionName);
  const child = section?.children?.find((el) => el.name === childName);
  const property = child?.properties?.find((el) => el.name === propertyName);
  return property?.value || fallback;
}

/** CSS custom properties for default / outline buttons. */
export function getButtonThemeVars(marketInfo) {
  const d = DEFAULT_BUTTON_THEME;
  return {
    "--button-default-background-color": getColorProperty(
      marketInfo,
      "Button",
      "Default",
      "Background Color",
      d.defaultBg
    ),
    "--button-default-text-color": getColorProperty(
      marketInfo,
      "Button",
      "Default",
      "Text Color",
      d.defaultText
    ),
    "--button-default-hover-background-color": getColorProperty(
      marketInfo,
      "Button",
      "Default",
      "Background Color on hover",
      d.defaultHoverBg
    ),
    "--button-default-hover-text-color": getColorProperty(
      marketInfo,
      "Button",
      "Default",
      "Text Color On hover",
      d.defaultHoverText
    ),
    "--button-default-outline-background-color": getColorProperty(
      marketInfo,
      "Button",
      "Outline",
      "Background Color",
      d.outlineBg
    ),
    "--button-default-outline-text-color": getColorProperty(
      marketInfo,
      "Button",
      "Outline",
      "Text Color",
      d.outlineText
    ),
  };
}

export function getWhiteLabelTermsLink(marketInfo) {
  return (
    marketInfo?.whiteLabel?.colorSchema?.whitelabelTandC || DEFAULT_LINKS.terms
  );
}

export function getWhiteLabelPrivacyLink(marketInfo) {
  return (
    marketInfo?.whiteLabel?.colorSchema?.whitelabelPrivacy ||
    DEFAULT_LINKS.privacy
  );
}

export function getIntroPageText(marketInfo, fallbackHtml) {
  const custom = marketInfo?.whiteLabel?.colorSchema?.whiteLabelIntroPageText;
  return custom || fallbackHtml;
}

function parseHexColor(color) {
  if (!color || typeof color !== "string") {
    return null;
  }
  let hex = color.trim();
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

/** True for white/near-white outline button backgrounds. */
export function isLightBackgroundColor(color) {
  const rgb = parseHexColor(color);
  if (!rgb) {
    return true;
  }
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.55;
}

/** Readable Connect label on exchange row; keeps outline background unchanged. */
export function getExchangeConnectLabelColor(marketInfo) {
  const outlineBg = getColorProperty(
    marketInfo,
    "Button",
    "Outline",
    "Background Color",
    DEFAULT_BUTTON_THEME.outlineBg
  );
  if (isLightBackgroundColor(outlineBg)) {
    return getColorProperty(
      marketInfo,
      "Button",
      "Outline",
      "Text Color",
      DEFAULT_BUTTON_THEME.outlineText
    );
  }
  return getColorProperty(
    marketInfo,
    "Button",
    "Default",
    "Text Color",
    DEFAULT_BUTTON_THEME.defaultText
  );
}

export function getExchangeConnectBorderColor(marketInfo) {
  const outlineBg = getColorProperty(
    marketInfo,
    "Button",
    "Outline",
    "Background Color",
    DEFAULT_BUTTON_THEME.outlineBg
  );
  if (isLightBackgroundColor(outlineBg)) {
    return getColorProperty(
      marketInfo,
      "Button",
      "Outline",
      "Text Color",
      DEFAULT_BUTTON_THEME.outlineText
    );
  }
  return outlineBg;
}
